import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Download, FileUp } from 'lucide-react';
import { useState } from 'react';
import type { ImportResult as Result } from '../api/subscribersApi';
import { downloadSampleCsv, MAX_CSV_BYTES } from '../model/sampleCsv';
import { ImportResult } from './ImportResult';

type Choice = { file: File | null; optedIn: boolean };
type FormProps = { choice: Choice; error: string | null; onChange: (c: Choice) => void };

const tooBig = (file: File | null) => file !== null && file.size > MAX_CSV_BYTES;

function ImportForm({ choice, error, onChange }: FormProps) {
  const { file, optedIn } = choice;
  return (
    <Stack sx={{ gap: 2, pt: 1 }}>
      <Typography variant="body2">
        A CSV file with a header row and the columns <b>name</b>, <b>phone</b> and <b>email</b>. Phone numbers need the country code
        (+65 9123 4567); email can be left empty. Numbers already on the list are skipped.
      </Typography>
      <Button variant="text" startIcon={<Download size={15} />} onClick={downloadSampleCsv} sx={{ alignSelf: 'flex-start' }}>
        Download sample CSV
      </Button>
      <Button component="label" variant="outlined" startIcon={<FileUp size={15} />} sx={{ alignSelf: 'flex-start' }}>
        {file ? file.name : 'Choose CSV file'}
        <input hidden type="file" accept=".csv,text/csv" onChange={(e) => { onChange({ ...choice, file: e.target.files?.[0] ?? null }); }} />
      </Button>
      {tooBig(file) ? <Alert severity="error">This file is over 1 MB. Split it into smaller files.</Alert> : null}
      <FormControlLabel
        control={<Checkbox checked={optedIn} onChange={(e) => { onChange({ ...choice, optedIn: e.target.checked }); }} />}
        label="Everyone in this file has agreed to receive WhatsApp updates"
      />
      {optedIn ? null : (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Without this, the clients are added but receive nothing until their consent is recorded.
        </Typography>
      )}
      {error ? <Alert severity="error">{error}</Alert> : null}
    </Stack>
  );
}

type Props = {
  listName: string;
  isPending: boolean;
  error: string | null;
  result: Result | undefined;
  onCancel: () => void;
  onImport: (csv: string, optedIn: boolean) => void;
};

/** Adds many clients at once from a CSV with name, phone and email columns, then shows what went in. */
export function ImportClientsDialog({ listName, isPending, error, result, onCancel, onImport }: Props) {
  const [choice, setChoice] = useState<Choice>({ file: null, optedIn: false });
  const submit = () => { if (choice.file) void choice.file.text().then((csv) => { onImport(csv, choice.optedIn); }); };

  return (
    <Dialog open onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Import clients into {listName}</DialogTitle>
      <DialogContent>{result ? <ImportResult result={result} listName={listName} /> : <ImportForm choice={choice} error={error} onChange={setChoice} />}</DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{result ? 'Done' : 'Cancel'}</Button>
        {result ? null : (
          <Button variant="contained" disabled={!choice.file || tooBig(choice.file) || isPending} onClick={submit}>Import</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
